/**
 * "Add brand" dialog — typeahead search across Facebook pages, then kicks
 * off the-core's onboarding pipeline for the selected page and routes the
 * user to the brand page in live-onboarding mode (`?job=<jobId>` triggers
 * polling + skeletons on BrandPage).
 *
 * Two paths:
 *   1. Paste a FB URL → skip search, onboard directly with the URL.
 *   2. Type a brand name → debounced typeahead, user clicks a candidate
 *      → onboard with that candidate's fb_page_id (deterministic, no
 *      "the-core picked the wrong page" surprises).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Search, Loader2, CheckCircle2, ChevronDown } from 'lucide-react'
import { toast } from 'sonner'

import * as API from '@/services/api.js'

import { handleError } from '@/services/errorHandler.js'
import { AnalyticsService } from '@/services/analytics.js'

import { gcsUriToHttp } from '../_lib/gcs.js'


const SEARCH_DEBOUNCE_MS = 280

const ease = 'easeOut'

const suggestionChipVariants = {
    hidden: { opacity: 0, y: 4 },
    show: { opacity: 1, y: 0, transition: { duration: 0.25, ease } },
}


// Country pool the user wants to search / onboard from. The Meta Ad
// Library's typeahead + ad results are scoped by country pool; "ALL"
// lets the user cast the widest net before picking a brand.
export const COUNTRIES = [
    { code: 'ALL', flag: '🌎', label: 'All countries' },
    { code: 'US', flag: '🇺🇸', label: 'United States' },
    { code: 'GB', flag: '🇬🇧', label: 'United Kingdom' },
    { code: 'CA', flag: '🇨🇦', label: 'Canada' },
    { code: 'AU', flag: '🇦🇺', label: 'Australia' },
    { code: 'DE', flag: '🇩🇪', label: 'Germany' },
    { code: 'FR', flag: '🇫🇷', label: 'France' },
    { code: 'IT', flag: '🇮🇹', label: 'Italy' },
    { code: 'ES', flag: '🇪🇸', label: 'Spain' },
    { code: 'NL', flag: '🇳🇱', label: 'Netherlands' },
    { code: 'BR', flag: '🇧🇷', label: 'Brazil' },
    { code: 'MX', flag: '🇲🇽', label: 'Mexico' },
    { code: 'IN', flag: '🇮🇳', label: 'India' },
    { code: 'JP', flag: '🇯🇵', label: 'Japan' },
    { code: 'IL', flag: '🇮🇱', label: 'Israel' },
]


function looksLikeUrl(v) {
    return /^https?:\/\//i.test(v) || v.includes('facebook.com') || v.includes('fb.com')
}


function formatFollowers(n) {
    if (!Number.isFinite(n) || n <= 0) {
        return null
    }
    if (n >= 1_000_000) {
        return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
    }
    if (n >= 1_000) {
        return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`
    }
    return String(n)
}


// Row-shaped skeleton — same dimensions as a real candidate/existing row so
// the dropdown height doesn't jump when real rows replace it. Pure CSS pulse,
// no framer-motion involvement (keeps it out of the AnimatePresence siblings
// list so it doesn't fight with the row enter/exit animations).
function SearchSkeletonRows({ count = 3 }) {
    return (
        <div>
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="flex w-full items-center gap-3 px-3 py-2.5">
                    <div className="h-9 w-9 shrink-0 animate-pulse rounded-md bg-foreground/[0.06]" />
                    <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="h-3 w-32 animate-pulse rounded bg-foreground/[0.06]" style={{ animationDelay: `${i * 80}ms` }} />
                        <div className="h-2.5 w-20 animate-pulse rounded bg-foreground/[0.06]" style={{ animationDelay: `${i * 80 + 40}ms` }} />
                    </div>
                </div>
            ))}
        </div>
    )
}


export function AddBrandDialog({ open, onClose }) {

    const { accountId, spaceId } = useParams()
    const navigate = useNavigate()

    const [input, setInput] = useState('')
    const [results, setResults] = useState([])
    // Advertisers already in our DB matching the query — surfaced as a
    // separate group above candidates so picking one navigates straight
    // to the brand page instead of re-triggering onboarding.
    const [existing, setExisting] = useState([])
    const [isSearching, setIsSearching] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [highlighted, setHighlighted] = useState(0)
    const [country, setCountry] = useState('US')
    const [countryOpen, setCountryOpen] = useState(false)
    // Quick-pick suggestions shown when the input is empty. Sampled from
    // advertisers already in the-core's DB so the user can jump straight
    // back into a brand they (or anyone) recently onboarded.
    const [suggestions, setSuggestions] = useState([])
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false)

    const abortRef = useRef(null)
    const abortExistingRef = useRef(null)
    const debounceRef = useRef(null)

    // Reset internal state on EVERY open toggle — both close and reopen.
    // useLayoutEffect (not useEffect) so the reset commits BEFORE the next
    // paint, otherwise the first frame of the enter animation flashes the
    // previous interaction's results and the dialog appears at the old size
    // before collapsing. Also aborts any in-flight searches so a late
    // response can't slip a result into the freshly-opened dialog.
    useLayoutEffect(() => {
        setInput('')
        setResults([])
        setExisting([])
        setHighlighted(0)
        setIsSearching(false)
        setCountryOpen(false)
        if (abortRef.current) {
            abortRef.current.abort()
            abortRef.current = null
        }
        if (abortExistingRef.current) {
            abortExistingRef.current.abort()
            abortExistingRef.current = null
        }
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
            debounceRef.current = null
        }
    }, [open])

    // Load suggestions when the dialog opens — quick-pick chips from
    // the-core's suggestions sample (advertisers with analyzed ads).
    useEffect(() => {
        if (!open) {
            return
        }
        let cancelled = false
        const load = async () => {
            setIsLoadingSuggestions(true)
            try {
                // Server-sampled random 8 (fresh seed per request → variety
                // per open). The old approach fetched the FULL advertiser
                // list (~15s at current corpus size) just to sample 8 chips.
                const { data } = await API.searchTheCoreAdvertiserSuggestions({ limit: 8 })
                if (cancelled) {
                    return
                }
                const rows = Array.isArray(data?.advertisers) ? data.advertisers : []
                setSuggestions(rows.map((r) => ({
                    page_id: r.fb_page_id ?? r.advertiser_id,
                    name: r.display_name,
                    picture_gcs: r.profile_picture_gcs,
                    vertical: r.vertical,
                })).filter((s) => s.page_id && s.name))
            } catch {
                setSuggestions([])
            } finally {
                if (!cancelled) {
                    setIsLoadingSuggestions(false)
                }
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [open])

    // Debounced search whenever the input changes. Fires TWO parallel
    // requests: an instant DB lookup (existing advertisers — typically
    // returns in ~200ms) and a slower Meta typeahead (candidates — 2-5s).
    // Each updates its own state slot independently so the dropdown can
    // render the DB suggestions immediately instead of waiting on Meta.
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
        }
        if (abortRef.current) {
            abortRef.current.abort()
            abortRef.current = null
        }
        if (abortExistingRef.current) {
            abortExistingRef.current.abort()
            abortExistingRef.current = null
        }

        const v = input.trim()
        if (!v || looksLikeUrl(v) || v.length < 2) {
            setResults([])
            setExisting([])
            setIsSearching(false)
            return
        }

        // Clear stale results SYNCHRONOUSLY on every input change so retyping
        // doesn't leave the previous query's rows visible during the debounce
        // + new-fetch window. Flip into "searching" at the same time so the
        // skeleton renders immediately (no "No matches" flash, no stale data).
        setExisting([])
        setResults([])
        setIsSearching(true)

        debounceRef.current = setTimeout(() => {
            // Track result counts from each branch so the analytics event
            // fired once both resolve can report the size of each search.
            let existingCount = 0
            let candidatesCount = 0

            // ── DB existing (fast) ──
            const existingController = new AbortController()
            abortExistingRef.current = existingController
            const existingP = (async () => {
                try {
                    const { data } = await API.searchTheCoreExistingBrands(v, { signal: existingController.signal })
                    if (existingController.signal.aborted) {
                        return
                    }
                    const rows = data.existing ?? []
                    setExisting(rows)
                    existingCount = rows.length
                } catch (e) {
                    if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError') {
                        return
                    }
                    // Soft-fail — candidates will still render below.
                    setExisting([])
                } finally {
                    if (abortExistingRef.current === existingController) {
                        abortExistingRef.current = null
                    }
                }
            })()

            // ── Meta candidates (slow) ──
            const candidatesController = new AbortController()
            abortRef.current = candidatesController
            const candidatesP = (async () => {
                try {
                    const { data } = await API.searchTheCoreBrands(v, { signal: candidatesController.signal, country })
                    if (candidatesController.signal.aborted) {
                        return
                    }
                    const rows = data.candidates ?? []
                    setResults(rows)
                    setHighlighted(0)
                    candidatesCount = rows.length
                } catch (e) {
                    if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError') {
                        return
                    }
                    handleError(e)
                    setResults([])
                } finally {
                    if (abortRef.current === candidatesController) {
                        abortRef.current = null
                    }
                    // Only the CURRENT search may clear the spinner. When the
                    // user retypes, the previous request is aborted and lands
                    // here too — clearing then would flash "No matches" while
                    // the new request is still in flight.
                    if (!candidatesController.signal.aborted) {
                        setIsSearching(false)
                    }
                }
            })()

            // Wait for both branches and fire a single committed-search event.
            // Skip when either branch was aborted (the user kept typing or
            // closed the dialog — that search wasn't really "completed").
            Promise.allSettled([existingP, candidatesP]).then(() => {
                if (existingController.signal.aborted || candidatesController.signal.aborted) {
                    return
                }
                AnalyticsService.track('intelligence-add-brand-searched', {
                    q: v,
                    country,
                    existing_count: existingCount,
                    candidates_count: candidatesCount,
                    total_count: existingCount + candidatesCount,
                })
            })
        }, SEARCH_DEBOUNCE_MS)

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
        }
    }, [input, country])

    const submit = async (body, displayHint, source = 'candidate') => {
        setIsSubmitting(true)
        try {
            // Country pool drives both the typeahead search AND the ad
            // library scrape. "ALL" tells the-core to default to its
            // global scan rather than US-only.
            AnalyticsService.track('intelligence-add-brand-submitted', {
                advertiserId: body.fbPageId,
                country,
                source,
                q: input.trim() || null,
            })
            const { data } = await API.onboardTheCoreBrand({ ...body, country })

            const advertiserId = data.advertiser_id
            const jobId = data.job_id
            if (!advertiserId) {
                throw new Error('the-core did not return an advertiser_id')
            }

            const target = `/account/${accountId}/space/${spaceId}/dashboard/intelligence/brands/${advertiserId}`
            const url = jobId && !data.short_circuited ? `${target}?job=${jobId}` : target

            // Only toast for fresh analyses — short-circuit (already
            // analyzed) just opens the page silently, no banner needed.
            if (!data.short_circuited) {
                const displayName = data.display_name ?? displayHint ?? 'Advertiser'
                toast.info(`Analyzing ${displayName} — we'll fill in as we go.`)
            }
            navigate(url)
            onClose()
            setInput('')
        } catch (e) {
            handleError(e)
            toast.error(e?.response?.data?.error?.message ?? e.message ?? 'Failed to start onboarding')
        } finally {
            setIsSubmitting(false)
        }
    }

    // Dedupe: strip any candidate from the Meta typeahead whose page_id
    // is already represented in the existing-DB rows. Otherwise the same
    // advertiser would appear twice — once under existing (fast-path) and
    // once under candidates (onboard-path).
    const filteredResults = useMemo(() => {
        if (existing.length === 0) {
            return results
        }
        const existingPageIds = new Set(
            existing.map((e) => String(e.fb_page_id ?? e.advertiser_id ?? '')).filter(Boolean),
        )
        return results.filter((c) => !existingPageIds.has(String(c.page_id)))
    }, [existing, results])

    const onPickCandidate = async (candidate) => {
        // Defensive fast-path: even if the candidate came from the Meta
        // typeahead, check the existing list — if we already have it in
        // our DB, skip the onboard call entirely. Covers the race where
        // the DB lookup finishes after the user clicks a Meta row.
        const existingMatch = existing.find(
            (e) => String(e.fb_page_id ?? e.advertiser_id ?? '') === String(candidate.page_id),
        )
        if (existingMatch) {
            onPickExisting(existingMatch)
            return
        }
        await submit({ fbPageId: candidate.page_id }, candidate.name)
    }

    // Pick an advertiser that already exists in our DB → navigate straight
    // to its brand page, skipping the onboard API call (which would just
    // short-circuit anyway and burn an unnecessary round-trip). The
    // advertiser_id in the-core IS the FB page id for external sources, so
    // we accept either field.
    const onPickExisting = (adv) => {
        const advertiserId = adv.advertiser_id ?? adv.fb_page_id ?? adv.page_id
        if (!advertiserId) {
            return
        }
        AnalyticsService.track('intelligence-add-brand-existing-picked', {
            advertiserId,
            q: input.trim() || null,
        })
        navigate(`/account/${accountId}/space/${spaceId}/dashboard/intelligence/brands/${advertiserId}`)
        onClose()
        setInput('')
    }

    // Auto-submit when the input looks like a URL — paste a FB page and we
    // kick off onboarding immediately, no button required.
    const onSubmitUrl = async () => {
        const v = input.trim()
        if (!v || !looksLikeUrl(v)) {
            return
        }
        await submit({ facebookPageUrl: v }, null, 'url-paste')
    }

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown' && results.length > 0) {
            e.preventDefault()
            setHighlighted((i) => (i + 1) % results.length)
            return
        }
        if (e.key === 'ArrowUp' && results.length > 0) {
            e.preventDefault()
            setHighlighted((i) => (i - 1 + results.length) % results.length)
            return
        }
        if (e.key === 'Enter') {
            e.preventDefault()
            // Enter only fires the URL path or picks the highlighted candidate.
            if (looksLikeUrl(input.trim())) {
                onSubmitUrl()
                return
            }
            if (results.length > 0 && results[highlighted]) {
                onPickCandidate(results[highlighted])
            }
        }
    }

    // Show the dropdown whenever the user has typed something searchable.
    // We surface empty-results explicitly inside the panel rather than
    // collapsing back to a blank dialog.
    const v = input.trim()
    const showDropdown = !looksLikeUrl(v) && v.length >= 2

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[10vh] backdrop-blur-sm"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-[720px] overflow-hidden rounded-2xl border border-foreground/25 bg-card shadow-lg"
                    >
                        <button
                            type="button"
                            onClick={onClose}
                            className="absolute right-4 top-4 z-10 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
                            aria-label="Close"
                        >
                            <X className="h-4 w-4" />
                        </button>

                        <div className="px-7 pt-7">
                            {/* <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
                                Add a brand
                            </div> */}
                            <h2 className="mt-1 text-[24px] leading-tight text-foreground">
                                Search for an advertiser
                            </h2>
                        </div>

                        <div className="mt-5 px-7">
                            <div className="flex items-center gap-3 rounded-full border border-foreground/40 bg-card px-4 py-1 transition-colors focus-within:border-ring">
                                {isSearching ? (
                                    <Loader2 className="h-5 w-5 shrink-0 animate-spin text-muted-foreground" strokeWidth={1.75} />
                                ) : (
                                    <Search className="h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                                )}
                                <input
                                    autoFocus
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={onKeyDown}
                                    placeholder="Search an advertiser, or paste a Facebook page URL"
                                    className="min-w-0 flex-1 border-0 bg-transparent py-1 text-[22px] leading-tight text-foreground outline-none ring-0 placeholder:text-muted-foreground focus:border-0 focus:outline-none focus:ring-0"
                                    disabled={isSubmitting}
                                />
                                <CountryPicker
                                    value={country}
                                    onChange={setCountry}
                                    open={countryOpen}
                                    setOpen={setCountryOpen}
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        {/* Dropdown wrapper — opacity-only fade. Height is driven by
                            content via CSS reflow; no framer-motion height animation
                            (those are janky with streaming async content). Inside, three
                            independent groups each fade in/out as their data arrives:
                            skeleton (initial), existing rows (DB), candidate rows (Meta). */}
                        {showDropdown && (
                            <motion.div
                                key={`dropdown-${input}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.15 }}
                                className="mt-4 border-t border-border"
                            >
                                <div className="max-h-[420px] overflow-y-auto px-3 py-2">
                                    {/* No matches — final state, both searches resolved empty */}
                                    {!isSearching && existing.length === 0 && filteredResults.length === 0 && (
                                        <div className="px-4 py-6 text-center text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                                            No matches
                                        </div>
                                    )}
                                    {/* Existing DB block — fades in as a whole when results arrive. */}
                                    {existing.length > 0 && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.18, ease: 'easeOut' }}
                                        >
                                            {existing.map((e) => {
                                                const picture = e.profile_picture_gcs
                                                    ? gcsUriToHttp(e.profile_picture_gcs)
                                                    : null
                                                return (
                                                    <button
                                                        key={`existing-${e.advertiser_id}`}
                                                        type="button"
                                                        onClick={() => onPickExisting(e)}
                                                        disabled={isSubmitting}
                                                        className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-foreground/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        {picture ? (
                                                            <img
                                                                src={picture}
                                                                alt={e.display_name ?? ''}
                                                                className="h-9 w-9 shrink-0 rounded-md object-cover"
                                                                loading="lazy"
                                                            />
                                                        ) : (
                                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-foreground/[0.06] text-[12px] uppercase text-muted-foreground">
                                                                {(e.display_name ?? '?')[0]}
                                                            </div>
                                                        )}
                                                        <div className="min-w-0 flex-1">
                                                            <div className="truncate text-[13.5px] font-medium text-foreground">
                                                                {e.display_name ?? e.advertiser_id}
                                                            </div>
                                                            <div className="mt-0.5 truncate text-[10.5px] uppercase tracking-[0.1em] text-muted-foreground">
                                                                {(() => {
                                                                    // Prefer Meta's live "active ads" count (same metric
                                                                    // the brand page surfaces). Fall back to the DB ad
                                                                    // count when onboarding hasn't populated the live
                                                                    // value yet, so the row never reads "0 ads".
                                                                    const count = (typeof e.active_ads_total === 'number' && e.active_ads_total >= 0)
                                                                        ? e.active_ads_total
                                                                        : e.ad_count
                                                                    return [
                                                                        e.vertical,
                                                                        count > 0 && `${count} ${count === 1 ? 'ad' : 'ads'}`,
                                                                    ].filter(Boolean).join(' · ')
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </button>
                                                )
                                            })}
                                        </motion.div>
                                    )}
                                    {/* Skeleton placeholders — sit in the candidates slot while
                                        Meta typeahead is still in flight (whether or not the DB
                                        block above has rendered yet). Real candidate rows replace
                                        them when the second search resolves, so the dropdown's
                                        height stays roughly stable across all three phases:
                                        initial-loading → DB-in → both-in. */}
                                    {isSearching && filteredResults.length === 0 && (
                                        <SearchSkeletonRows count={existing.length > 0 ? 3 : 4} />
                                    )}
                                    {/* Candidates block — fades in as a whole when Meta returns. */}
                                    {filteredResults.length > 0 && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.18, ease: 'easeOut' }}
                                        >
                                            {filteredResults.map((c, idx) => {
                                                const isHi = idx === highlighted
                                                const followers = formatFollowers(c.likes)
                                                const igFollowers = formatFollowers(c.ig_followers)
                                                // Country code → display label via the COUNTRIES table,
                                                // falling back to the FB page alias. Disambiguates global
                                                // brands that have the same name across regions.
                                                const countryLabel = COUNTRIES.find((co) => co.code === c.country)?.label
                                                const subLabel = countryLabel ?? c.page_alias ?? null
                                                return (
                                                    <button
                                                        key={c.page_id}
                                                        type="button"
                                                        onMouseEnter={() => setHighlighted(idx)}
                                                        onClick={() => onPickCandidate(c)}
                                                        disabled={isSubmitting}
                                                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left disabled:cursor-not-allowed disabled:opacity-50 ${isHi ? 'bg-foreground/[0.05]' : 'hover:bg-foreground/[0.04]'}`}
                                                    >
                                                        {c.picture_url ? (
                                                            <img
                                                                src={c.picture_url}
                                                                alt={c.name}
                                                                className="h-9 w-9 shrink-0 rounded-md object-cover"
                                                                loading="lazy"
                                                            />
                                                        ) : (
                                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-foreground/[0.06] text-[12px] uppercase text-muted-foreground">
                                                                {c.name?.[0] ?? '?'}
                                                            </div>
                                                        )}
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="truncate text-[13.5px] font-medium text-foreground">{c.name}</span>
                                                                {c.verification === 'BLUE_VERIFIED' && (
                                                                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-secondary" strokeWidth={2.5} />
                                                                )}
                                                                {subLabel && (
                                                                    <span className="shrink-0 text-[12px] text-muted-foreground">({subLabel})</span>
                                                                )}
                                                            </div>
                                                            <div className="mt-0.5 truncate text-[10.5px] uppercase tracking-[0.1em] text-muted-foreground">
                                                                {[c.category, followers && `${followers} likes`, igFollowers && `IG ${igFollowers}`].filter(Boolean).join(' · ')}
                                                            </div>
                                                        </div>
                                                        {isHi && !isSubmitting && (
                                                            <span className="text-[9.5px] uppercase tracking-[0.16em] text-muted-foreground">
                                                                ↵ pick
                                                            </span>
                                                        )}
                                                    </button>
                                                )
                                            })}
                                        </motion.div>
                                    )}
                                </div>
                            </motion.div>
                        )}

                        {/* When the input is empty + dropdown isn't open, show
                            a row of quick-pick suggestion chips so the user
                            can jump straight into an existing brand without
                            typing. Falls back to a small spacer when there
                            are no suggestions (very fresh dev DBs). */}
                        <AnimatePresence initial={false}>
                            {!showDropdown && (isLoadingSuggestions || suggestions.length > 0) && (
                                <motion.div
                                    key="brand-suggestions"
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.18, ease }}
                                    className="mt-5 overflow-hidden border-t border-border px-7 pb-6 pt-4"
                                >
                                    <div className="mb-2.5 text-[9.5px] uppercase tracking-[0.18em] text-muted-foreground">
                                        Suggestions
                                    </div>
                                    <motion.div
                                        key={isLoadingSuggestions ? 'loading' : 'loaded'}
                                        initial="hidden"
                                        animate="show"
                                        variants={{
                                            hidden: {},
                                            show: { transition: { staggerChildren: 0.04 } },
                                        }}
                                        className="flex flex-wrap gap-2"
                                    >
                                        {isLoadingSuggestions
                                            ? Array.from({ length: 6 }).map((_, i) => (
                                                <motion.div
                                                    key={i}
                                                    variants={suggestionChipVariants}
                                                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card py-1 pl-1 pr-3"
                                                    style={{ minWidth: `${90 + (i % 3) * 30}px` }}
                                                >
                                                    <span className="h-6 w-6 shrink-0 animate-pulse rounded-full bg-foreground/[0.08]" />
                                                    <span className="h-3 w-full animate-pulse rounded bg-foreground/[0.08]" />
                                                </motion.div>
                                            ))
                                            : suggestions.map((s) => (
                                                <motion.button
                                                    key={s.page_id}
                                                    type="button"
                                                    variants={suggestionChipVariants}
                                                    onClick={() => onPickExisting({ advertiser_id: s.page_id, display_name: s.name })}
                                                    disabled={isSubmitting}
                                                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card py-1 pl-1 pr-3 text-left transition-colors hover:border-foreground/25 hover:bg-foreground/[0.03] disabled:cursor-not-allowed disabled:opacity-50"
                                                    title={s.vertical ? `${s.name} · ${s.vertical}` : s.name}
                                                >
                                                    {s.picture_gcs ? (
                                                        <img
                                                            src={gcsUriToHttp(s.picture_gcs)}
                                                            alt=""
                                                            loading="lazy"
                                                            className="h-6 w-6 shrink-0 rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-foreground/[0.06] text-[10.5px] uppercase text-muted-foreground">
                                                            {s.name?.[0] ?? '?'}
                                                        </span>
                                                    )}
                                                    <span className="max-w-[140px] truncate text-[12.5px] text-foreground">
                                                        {s.name}
                                                    </span>
                                                </motion.button>
                                            ))}
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                        {!showDropdown && !isLoadingSuggestions && suggestions.length === 0 && (
                            <div className="h-6" />
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}


// Compact pill country selector — flag + code on the trigger, popover
// list with flag + full name on click. The popover is portaled to
// document.body so it escapes the modal's overflow-hidden clip context
// (the search-results panel needs that clip for its height animation).
// Scopes both the typeahead and the eventual ad-library scrape to the
// chosen Meta Ad Library pool.
const POPOVER_WIDTH = 220
const POPOVER_MAX_HEIGHT = 280
const POPOVER_GAP = 6

export function CountryPicker({ value, onChange, open, setOpen, disabled, excludeAll = false, tone = 'light', size = 'md' }) {

    // The Re-sync use case requires a specific Meta pool, so it filters out
    // the "ALL" pseudo-country (which only makes sense for typeahead).
    const list = excludeAll ? COUNTRIES.filter(c => c.code !== 'ALL') : COUNTRIES
    const current = list.find(c => c.code === value) ?? list[0]
    const triggerRef = useRef(null)
    const popoverRef = useRef(null)
    const [popoverPos, setPopoverPos] = useState(null)

    // Position the popover anchored to the trigger. Flips above when there
    // isn't enough room below (modal sits ~10vh from top so bottom-flip
    // basically never kicks in, but it's free insurance).
    useLayoutEffect(() => {
        if (!open || !triggerRef.current) {
            return
        }
        const update = () => {
            const rect = triggerRef.current.getBoundingClientRect()
            const spaceBelow = window.innerHeight - rect.bottom
            const flipUp = spaceBelow < POPOVER_MAX_HEIGHT + POPOVER_GAP && rect.top > POPOVER_MAX_HEIGHT
            const top = flipUp
                ? rect.top - POPOVER_MAX_HEIGHT - POPOVER_GAP
                : rect.bottom + POPOVER_GAP
            // Right-align to the trigger, clamp to viewport so we don't bleed off the edge.
            const right = Math.max(8, window.innerWidth - rect.right)
            setPopoverPos({ top, right })
        }
        update()
        window.addEventListener('resize', update)
        window.addEventListener('scroll', update, true)
        return () => {
            window.removeEventListener('resize', update)
            window.removeEventListener('scroll', update, true)
        }
    }, [open])

    // Close when clicking outside either the trigger or the popover.
    useEffect(() => {
        if (!open) {
            return
        }
        const onDocClick = (e) => {
            if (triggerRef.current?.contains(e.target)) {
                return
            }
            if (popoverRef.current?.contains(e.target)) {
                return
            }
            setOpen(false)
        }
        document.addEventListener('mousedown', onDocClick)
        return () => document.removeEventListener('mousedown', onDocClick)
    }, [open, setOpen])

    // Trigger styling — light is the dialog default; dark variant matches
    // the BrandHero's paper-on-color buttons. Sized to match the row of
    // adjacent action buttons in each context (h-9 dialog, h-8 hero).
    const sizeClass = size === 'sm' ? 'h-8 px-2.5' : 'h-9 px-3'
    const toneTrigger = tone === 'dark'
        ? 'border-white/30 bg-white/[0.08] text-white hover:border-white/50 hover:bg-white/[0.12]'
        : 'border-foreground/25 bg-card text-foreground hover:border-foreground/50'
    const chevronTone = tone === 'dark' ? 'text-white/70' : 'text-muted-foreground'
    return (
        <div className="relative shrink-0">
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen(!open)}
                disabled={disabled}
                className={`flex items-center gap-1.5 rounded-full border text-[12.5px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${sizeClass} ${toneTrigger}`}
            >
                <span className="text-[14px] leading-none">{current.flag}</span>
                <span className="text-[11px] uppercase tracking-[0.1em]">{current.code}</span>
                <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''} ${chevronTone}`} />
            </button>
            {typeof document !== 'undefined' && createPortal(
                <AnimatePresence>
                    {open && popoverPos && (
                        <motion.div
                            ref={popoverRef}
                            initial={{ opacity: 0, y: -4, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -4, scale: 0.98 }}
                            transition={{ duration: 0.14, ease: 'easeOut' }}
                            style={{
                                position: 'fixed',
                                top: popoverPos.top,
                                right: popoverPos.right,
                                width: POPOVER_WIDTH,
                                maxHeight: POPOVER_MAX_HEIGHT,
                                zIndex: 60,
                            }}
                            className="overflow-y-auto rounded-xl border border-foreground/25 bg-card p-1 shadow-lg"
                        >
                            {list.map(c => {
                                const isActive = c.code === value
                                return (
                                    <button
                                        key={c.code}
                                        type="button"
                                        onClick={() => {
                                            onChange(c.code)
                                            setOpen(false)
                                        }}
                                        className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-[13px] transition-colors ${isActive ? 'bg-foreground/[0.06] text-foreground' : 'text-foreground hover:bg-foreground/[0.04]'
                                            }`}
                                    >
                                        <span className="text-[14px] leading-none">{c.flag}</span>
                                        <span className="min-w-0 flex-1 truncate">{c.label}</span>
                                        <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{c.code}</span>
                                    </button>
                                )
                            })}
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body,
            )}
        </div>
    )
}
